import isEqual from 'lodash/isEqual';

import {
  LENS_PERIOD_KEY,
  type LensFilter,
  type LensFilterValues,
  type LensInsightScope,
  type LensPeriod,
} from '~/types/leLens';

import type { LensRange, LensSelection } from './cardView';
import { spanLabel } from './lensCalendar';
import { mergePeriods } from './lensPeriodPresets';

function comparableFilterValue(value: LensFilterValues[string] | undefined): unknown {
  if (Array.isArray(value)) {
    return value.length ? [...new Set(value)].sort() : undefined;
  }
  if (typeof value === 'string') {
    return value ? [value] : undefined;
  }
  if (typeof value === 'object' && value && !Object.keys(value).length) {
    return undefined;
  }
  if (typeof value === 'object' && value && ('from' in value || 'to' in value)) {
    return value.from || value.to ? { from: value.from || undefined, to: value.to || undefined } : undefined;
  }
  return value || undefined;
}

function comparableInsightScope(scope: LensInsightScope) {
  return {
    filters: Object.fromEntries(
      Object.entries(scope.effectiveFilters)
        .map(([key, value]) => [key, comparableFilterValue(value)])
        .filter(([, value]) => value !== undefined),
    ),
    ranges: Object.fromEntries(
      Object.entries(scope.ranges ?? {})
        .filter(([, range]) => range.min !== undefined || range.max !== undefined)
        .map(([key, range]) => [key, { min: range.min, max: range.max }]),
    ),
  };
}

/** A refreshed note keeps its actual generating scope after the reader restores the opening view. */
export function lensInsightScopeNotice(
  saved: LensInsightScope | undefined,
  current: LensInsightScope | undefined,
  changedOpeningScope: boolean,
): string | undefined {
  const staticNote = 'This text does not update when you change dates, comparisons or filters.';
  if (!saved) {
    return changedOpeningScope ? `Saved note. ${staticNote}` : undefined;
  }
  if (!current || isEqual(comparableInsightScope(saved), comparableInsightScope(current))) {
    return undefined;
  }
  const period = saved.effectiveFilters[LENS_PERIOD_KEY];
  if (period && typeof period === 'object' && !Array.isArray(period) && 'window' in period) {
    const window = period.window;
    const comparison = period.comparison;
    const range =
      window && typeof window === 'object' && 'from' in window
        ? spanLabel(window.from, window.to, 'always')
        : 'every available date';
    const compared =
      comparison && typeof comparison === 'object' && 'from' in comparison
        ? `, compared with ${spanLabel(comparison.from, comparison.to, 'always')}`
        : ', without a comparison';
    return `Saved note for ${range}${compared} and its recorded filters. ${staticNote}`;
  }
  return `Saved note for a different view. ${staticNote}`;
}

/** Older authored insight text has no recorded scope. Only flag changes to the opening view;
 * never claim that the note was authored for a particular period or set of filters. */
export function savedInsightScopeChanged({
  filters,
  values,
  boardPeriod,
  viewerPeriod,
  selections,
  ranges,
}: {
  filters: Array<LensFilter>;
  values: LensFilterValues;
  boardPeriod: LensPeriod;
  viewerPeriod: LensPeriod;
  selections: LensSelection;
  ranges: Record<string, LensRange>;
}): boolean {
  return (
    !isEqual(mergePeriods(boardPeriod), mergePeriods(viewerPeriod)) ||
    filters.some(
      (filter) => !isEqual(comparableFilterValue(values[filter.id]), comparableFilterValue(filter.defaultValue)),
    ) ||
    Object.values(selections).some((selection) => selection.length > 0) ||
    Object.values(ranges).some((range) => range.min !== undefined || range.max !== undefined)
  );
}
